/**
 * Global Snackbar Context
 *
 * Provides showSnackbar / showError / showSuccess / showInfo from anywhere.
 */

import { createContext, useContext, useState, useCallback, useMemo } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const GlobalSnackbarContext = createContext(null);
GlobalSnackbarContext.displayName = "GlobalSnackbarContext";

export function GlobalSnackbarProvider({ children }) {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
    duration: 10000,
  });

  const showSnackbar = useCallback((message, severity = "info", duration = 10000) => {
    setSnackbar({ open: true, message, severity, duration });
  }, []);

  const showSuccess = useCallback((msg) => showSnackbar(msg, "success"), [showSnackbar]);
  const showError = useCallback((msg) => showSnackbar(msg, "error", 10000), [showSnackbar]);
  const showWarning = useCallback((msg) => showSnackbar(msg, "warning"), [showSnackbar]);
  const showInfo = useCallback((msg) => showSnackbar(msg, "info"), [showSnackbar]);

  const handleClose = useCallback((_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const value = useMemo(
    () => ({ showSnackbar, showSuccess, showError, showWarning, showInfo }),
    [showSnackbar, showSuccess, showError, showWarning, showInfo]
  );

  return (
    <GlobalSnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </GlobalSnackbarContext.Provider>
  );
}

export function useSnackbar() {
  const context = useContext(GlobalSnackbarContext);
  if (!context) throw new Error("useSnackbar must be used within GlobalSnackbarProvider");
  return context;
}

export default GlobalSnackbarContext;
